import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Headphones, Sparkles, Clock } from "lucide-react";
import { ASSETS } from "@/lib/assets";
import { TopBar, Footer } from "@/components/Brand";
import StaggerText from "@/components/StaggerText";

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-cream relative overflow-hidden">
      {/* organic wave background */}
      <svg className="pointer-events-none absolute -top-32 -left-32 w-[1100px] opacity-[0.08]" viewBox="0 0 600 600" fill="none">
        <path d="M50 300 Q 200 100 350 300 T 650 300" stroke="#0E0E0E" strokeWidth="1" />
        <path d="M0 360 Q 200 160 350 360 T 700 360" stroke="#0E0E0E" strokeWidth="1" />
        <path d="M-50 420 Q 150 220 300 420 T 600 420" stroke="#0E0E0E" strokeWidth="1" />
      </svg>

      <TopBar />

      <main className="relative">
        <section className="px-6 sm:px-10 lg:px-16 pt-6 sm:pt-10 pb-24">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 relative">
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="font-sans text-[11px] sm:text-xs tracking-[0.35em] uppercase text-sage-700 mb-6 flex items-center gap-3"
              >
                <span className="h-px w-10 bg-sage-700/60" />
                A Free 2-Minute Diagnostic
              </motion.p>

              <StaggerText
                as="h1"
                testId="landing-headline"
                text={"What’s Secretly Draining Your Energy?"}
                className="font-display text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-[5rem] tracking-tight text-ink"
              />

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.7 }}
                className="mt-6 max-w-xl font-sans text-base sm:text-lg text-ink/75 leading-relaxed"
              >
                You’re capable. You’re driven. So why does it still feel heavy sometimes?
                <br />
                <span className="italic-display text-ink/85">
                  Discover the subconscious pattern quietly running your nervous system — and the meditation that resets it.
                </span>
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.9 }}
                className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5"
              >
                <button
                  type="button"
                  onClick={() => navigate("/quiz")}
                  className="group inline-flex items-center gap-3 bg-ink text-cream px-7 py-4 rounded-sm font-sans text-sm tracking-[0.18em] uppercase hover:bg-sage-700 transition-colors duration-500"
                  data-testid="start-quiz-button"
                >
                  Start the Quiz
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
                <div className="flex items-center gap-5 font-sans text-xs text-ink/55">
                  <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> 2 minutes</span>
                  <span className="inline-flex items-center gap-1.5"><Headphones className="h-3.5 w-3.5" /> Mind Magic Audio</span>
                  <span className="inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Personalized</span>
                </div>
              </motion.div>

              <div className="mt-14 max-w-xl border-t border-cream-300 pt-6">
                <p className="font-sans text-xs tracking-[0.25em] uppercase text-ink/40 mb-3">What you’ll uncover</p>
                <ul className="font-sans text-sm text-ink/70 space-y-2">
                  <li className="flex gap-3"><span className="text-sage-700">—</span> The exact archetype your nervous system has been running on autopilot</li>
                  <li className="flex gap-3"><span className="text-sage-700">—</span> Why “more productivity” never made it stop</li>
                  <li className="flex gap-3"><span className="text-sage-700">—</span> A custom Mind Magic Audio matched to your pattern — yours, free</li>
                </ul>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                className="relative"
              >
                <div className="relative rounded-sm overflow-hidden bg-ink aspect-[4/5]">
                  <img
                    src={ASSETS.hero_bw_serious}
                    alt="Chelsea Litsey"
                    className="absolute inset-0 h-full w-full object-cover object-top"
                    data-testid="landing-hero-image"
                  />
                  <div className="absolute inset-0" style={{background: "linear-gradient(to top, rgba(14,14,14,0.85) 0%, rgba(14,14,14,0.35) 35%, transparent 60%)"}}></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-cream-100">
                    <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-sage-300 mb-2">Created by</p>
                    <p className="font-display text-2xl tracking-tight">Chelsea Litsey</p>
                    <p className="font-sans text-xs text-cream-100/80 mt-0.5">NLP · QTT-certified Clarity Coach</p>
                  </div>
                </div>
                <div className="absolute -bottom-6 -left-6 bg-sage-500 text-cream-100 px-5 py-3 rounded-sm rotate-[-2deg] shadow-lg">
                  <p className="font-display italic-display text-xl">“Your body has been keeping the score.”</p>
                </div>
                <div className="absolute -top-6 -right-2 sm:-right-6 bg-cream-100 border border-sage-300 px-4 py-3 rounded-sm rotate-[3deg]">
                  <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-sage-700">5 Archetypes</p>
                  <p className="font-display text-lg text-ink">One is yours.</p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="px-6 sm:px-10 lg:px-16 pb-20">
          <div className="max-w-7xl mx-auto wave-divider h-10 mb-16" />
          <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-10">
            {[
              { n: "01", t: "Answer 6 quick questions", d: "Each one points to a different way your nervous system is asking you to slow down." },
              { n: "02", t: "Meet your Energy Archetype", d: "The Prover. The Over-Responsible One. The Invisible Achiever. The Self-Doubter. The Hyper-Aware Achiever." },
              { n: "03", t: "Receive your Mind Magic Audio", d: "A guided audio designed to calm the exact pattern — yours instantly, no purchase required." },
            ].map((s) => (
              <div key={s.n} className="relative">
                <p className="font-display italic-display text-5xl text-sage-500 mb-2">{s.n}</p>
                <h3 className="font-display text-2xl text-ink mb-2">{s.t}</h3>
                <p className="font-sans text-sm text-ink/70 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="px-6 sm:px-10 lg:px-16 pb-24">
          <div className="max-w-3xl mx-auto text-center relative">
            <p className="font-display italic-display text-2xl sm:text-3xl text-ink leading-snug">
              “I felt stuck and lost. Working with Chelsea was the ultimate life reset.
              I’m more energized, more passionate, and making more money than ever before.”
            </p>
            <p className="mt-5 font-sans text-xs tracking-[0.3em] uppercase text-ink/55">
              Jacqui Edgerly — Founder, Gut Health Jac
            </p>
          </div>
        </section>

        <section className="px-6 sm:px-10 lg:px-16 pb-24">
          <div className="max-w-7xl mx-auto bg-ink text-cream-100 rounded-sm p-10 sm:p-16 relative overflow-hidden">
            <div className="grid md:grid-cols-2 gap-10 items-center relative z-10">
              <div>
                <p className="font-sans text-[11px] tracking-[0.35em] uppercase text-sage-300 mb-4">Ready when you are</p>
                <h2 className="font-display text-4xl sm:text-5xl tracking-tight">Your nervous system already knows the answer.</h2>
                <p className="mt-4 font-sans text-cream-100/70 max-w-md">It only takes two minutes to give it the words.</p>
              </div>
              <div className="md:text-right">
                <button
                  type="button"
                  onClick={() => navigate("/quiz")}
                  className="inline-flex items-center gap-3 bg-sage-500 text-ink px-7 py-4 rounded-sm font-sans text-sm tracking-[0.18em] uppercase hover:bg-cream-100 transition-colors duration-500"
                  data-testid="start-quiz-button-bottom"
                >
                  Take the Quiz — Free
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
            <svg className="absolute -right-20 -bottom-20 w-[600px] opacity-10" viewBox="0 0 400 400" fill="none">
              <circle cx="200" cy="200" r="180" stroke="#8EA78F" strokeWidth="1" />
              <circle cx="200" cy="200" r="140" stroke="#8EA78F" strokeWidth="1" />
              <circle cx="200" cy="200" r="100" stroke="#8EA78F" strokeWidth="1" />
            </svg>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
